import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useT } from '@/i18n';
import { api, type Instance } from '@/lib/api';
import { cn } from '@/lib/cn';
import { adminGroup, isNavGroup, navEntries } from './nav-config';

interface FlatItem {
  label: string;
  to: string;
  group: string;
  hint?: string;
}

/**
 * Schnellzugriff ueber Strg+K.
 *
 * Neben den festen Zielen werden auch die eigenen Instanzen durchsucht, damit
 * man bei vielen Instanzen direkt zur richtigen springen kann.
 */
export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const [instances, setInstances] = useState<Instance[]>([]);
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const t = useT();

  // Instanzen erst beim ersten Oeffnen laden
  useEffect(() => {
    if (!open || instances.length > 0) return;
    api.instances
      .list()
      .then(({ instances: list }) => setInstances(list))
      .catch(() => undefined);
  }, [open, instances.length]);

  const items = useMemo<FlatItem[]>(() => {
    const staticItems: FlatItem[] = navEntries
      .filter((entry) => !isNavGroup(entry))
      .map((entry) => ({
        label: t((entry as { labelKey: Parameters<typeof t>[0] }).labelKey),
        to: (entry as { to: string }).to,
        group: t('nav.groupNavigation'),
      }));

    if (isAdmin) {
      staticItems.push(
        ...adminGroup.items.map((item) => ({
          label: t(item.labelKey),
          to: item.to,
          group: t('nav.admin'),
        })),
      );
    }

    const instanceItems: FlatItem[] = instances.map((instance) => ({
      label: instance.name,
      to: `/instanzen/${instance.id}`,
      group: t('nav.groupInstances'),
      hint: `${instance.ifName} - ${instance.ipv4Cidr}`,
    }));

    return [...staticItems, ...instanceItems];
  }, [instances, isAdmin, t]);

  const results = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return items.slice(0, 8);
    return items
      .filter((item) =>
        `${item.label} ${item.group} ${item.hint ?? ''}`.toLowerCase().includes(needle),
      )
      .slice(0, 20);
  }, [query, items]);

  useEffect(() => setActiveIdx(0), [query, open]);
  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  if (!open) return null;

  const go = (item: FlatItem) => {
    navigate(item.to);
    onClose();
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIdx((index) => Math.min(results.length - 1, index + 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIdx((index) => Math.max(0, index - 1));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const target = results[activeIdx];
      if (target) go(target);
    } else if (event.key === 'Escape') {
      onClose();
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-start justify-center bg-black/60 p-4 pt-[12vh] backdrop-blur-sm">
      <div className="absolute inset-0" onClick={onClose} aria-hidden />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('nav.quickSearch')}
        className="animate-vela-pop relative w-full max-w-lg overflow-hidden rounded-[var(--radius-vela-lg)] border border-line bg-bg-2 shadow-[var(--shadow-vela)]"
      >
        <input
          autoFocus
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={onKeyDown}
          placeholder={t('nav.quickSearch')}
          className="w-full border-b border-line bg-transparent px-4 py-3.5 text-sm text-t0 outline-none placeholder:text-t2"
        />

        <div className="max-h-[320px] overflow-y-auto p-1.5">
          {results.length === 0 && (
            <p className="px-3 py-6 text-center text-sm text-t2">{t('nav.noResults')}</p>
          )}
          {results.map((item, index) => (
            <button
              key={`${item.group}-${item.to}`}
              type="button"
              onMouseEnter={() => setActiveIdx(index)}
              onClick={() => go(item)}
              className={cn(
                'flex w-full items-center justify-between gap-3 rounded-[10px] px-3 py-2.5 text-left text-[13px]',
                index === activeIdx ? 'bg-acc-soft text-acc' : 'text-t1 hover:bg-bg-3',
              )}
            >
              <span className="min-w-0">
                <span className="block truncate font-semibold">{item.label}</span>
                {item.hint && <span className="block truncate font-mono text-[11px] text-t2">{item.hint}</span>}
              </span>
              <span className="shrink-0 text-[10.5px] font-bold tracking-wide text-t2 uppercase">
                {item.group}
              </span>
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3 border-t border-line px-4 py-2 text-[10.5px] text-t2">
          <span>{t('nav.quickHintNav')}</span>
          <span>{t('nav.quickHintOpen')}</span>
          <span className="ml-auto">{t('nav.quickHintClose')}</span>
        </div>
      </div>
    </div>,
    document.body,
  );
}
